// ── Extracted types from input.ts ──────────────────────────────────
// Re-exported from input.ts for backward compatibility.

import { activeBoardPixelSize } from "./constants";
import type { MoveAttempt, Position, PowerupKind, RotationDir } from "./types";

export const SWIPE_INTENT_THRESHOLD_TILE_FRACTION = 0.2;
export const SWIPE_INTENT_AXIS_DOMINANCE_RATIO = 1.15;
export const SWIPE_INTENT_RELOCK_NEUTRAL_FRACTION = 0.1;

export type SwipeIntentDirection = "LEFT" | "RIGHT" | "UP" | "DOWN";

export type UiAction =
  | "START"
  | "CONTINUE"
  | "LEADERBOARD_TOGGLE"
  | "GAMEOVER_NEXT"
  | "VERSUS_RESULT_NEXT"
  | "OPEN_SOLO_SELECTOR"
  | "CLOSE_SOLO_SELECTOR"
  | "SOLO_CAROUSEL_PREV"
  | "SOLO_CAROUSEL_NEXT"
  | "SOLO_SELECT_BACK"
  | "START_SOLO_CLASSIC"
  | "START_SOLO_ROW_RUSH"
  | "START_SOLO_CLEAROUT"
  | "START_SOLO_COLOR_HUNT"
  | "START_SOLO_TURBOMAXXING"
  | "CONTINUE_SOLO_CLASSIC"
  | "CONTINUE_SOLO_ROW_RUSH"
  | "CONTINUE_SOLO_CLEAROUT"
  | "CONTINUE_SOLO_COLOR_HUNT"
  | "CONTINUE_SOLO_TURBOMAXXING"
  | "SOLO_LEADERBOARD_CLASSIC"
  | "SOLO_LEADERBOARD_ROW_RUSH"
  | "SOLO_LEADERBOARD_CLEAROUT"
  | "SOLO_LEADERBOARD_COLOR_HUNT"
  | "SOLO_LEADERBOARD_TURBOMAXXING"
  | "SOLO_LEADERBOARD_CYCLE_NEXT"
  | "TOGGLE_MUSIC"
  | "TOGGLE_SFX"
  | "TOGGLE_HAPTICS"
  | "TOGGLE_PAUSE"
  | "SKIP_TUTORIAL"
  | "ACTIVATE_GROOVE"
  | "ROTATE_LEFT_NOW"
  | "ROTATE_RIGHT_NOW"
  | "ARM_POWERUP_HINT"
  | "ARM_POWERUP_ROWRIP"
  | "ARM_POWERUP_COLCUT"
  | "ARM_POWERUP_TRANSMUTE"
  | "ARM_POWERUP_ZIGZAGBOLT"
  | "CANCEL_POWERUP"
  | "START_TUTORIAL"
  | "LOGIN_GOOGLE"
  | "LOGIN_DISCORD"
  | "LOGOUT"
  | "MAIN_MENU"
  | "OPEN_SETTINGS"
  | "CLOSE_SETTINGS"
  | "TOGGLE_SETTINGS"
  | "SETTINGS_PRESET_NEXT"
  | "SETTINGS_SIDEFX_NEXT"
  | "SETTINGS_SIDEFX_DENSITY_NEXT"
  | "SETTINGS_PAGE_PREV"
  | "SETTINGS_PAGE_NEXT"
  | "SETTINGS_BG_STYLE_NEXT"
  | "SETTINGS_BG_BLOOM_NEXT"
  | "SETTINGS_BG_GLOW_NEXT"
  | "SETTINGS_BG_SATURATION_NEXT"
  | "SETTINGS_BG_MIX_NEXT"
  | "SETTINGS_BG_MOTION_NEXT"
  | "SETTINGS_PARTICLES_NEXT"
  | "SETTINGS_OPPONENT_NEXT"
  | "SETTINGS_FPSCAP_NEXT"
  | "SETTINGS_DEBUG_ENABLED_TOGGLE"
  | "SETTINGS_DEBUG_OVERLAY_GAMEPLAY_TOGGLE"
  | "SETTINGS_DEBUG_OVERLAY_BOT_TOGGLE"
  | "SETTINGS_DEBUG_VERBOSITY_NEXT"
  | "SETTINGS_DEBUG_DIAGNOSTICS_NEXT"
  | "SETTINGS_DEBUG_PERFSAFE_TOGGLE"
  | "EDIT_PROFILE"
  | "VIEW_LEADERBOARD"
  | "LEADERBOARD_TAB_SOLO"
  | "LEADERBOARD_TAB_VERSUS"
  | "VERSUS_MODE"
  | "TRAP_LOCKDOWN"
  | "TRAP_FOG"
  | "TRAP_COIN_TAX"
  | "TRAP_GROOVE_DRAIN"
  | "TRAP_DOOM_SURGE"
  | "VERSUS_READY"
  | "VERSUS_LEAVE"
  | "VERSUS_QUICKMATCH"
  | "VERSUS_CHAT"
  | "VERSUS_CREATE_LOBBY"
  | "VERSUS_JOIN_LOBBY"
  | "LOBBY_TOGGLE_JOIN"
  | "VERSUS_TAP_LOBBY_ROW"
  | "VERSUS_REFRESH_LOBBIES"
  | "VERSUS_FORFEIT"
  | "CLAIM_USERNAME"
  | "FOCUS_USERNAME_INPUT"
  | "OPEN_FRIENDS"
  | "OPEN_ADD_FRIEND"
  | "ADD_FRIEND_BACK"
  | "ADD_FRIEND"
  | "FRIEND_BATTLE"
  | "FRIEND_ACCEPT"
  | "FRIEND_DECLINE"
  | "FRIEND_REMOVE"
  | "FRIEND_REMOVE_CONFIRM"
  | "FRIEND_REMOVE_CANCEL"
  | "FRIENDS_PAGE_PREV"
  | "FRIENDS_PAGE_NEXT"
  | "OPEN_SHOP"
  | "SHOP_BUY"
  | "SHOP_EQUIP"
  | "SHOP_EXCHANGE"
  | "SHOP_BUY_GEMS"
  | "SHOP_BUY_SOL"
  | "SHOP_BUY_IAP"
  | "SHOP_RESTORE_IAP"
  | "SHOP_IAP_CONFIRM"
  | "SHOP_IAP_CANCEL"
  | "OPEN_GEM_SHOP"
  | "OPEN_PROFILE"
  | "PROFILE_EQUIP"
  | "PROFILE_PAGE_PREV"
  | "PROFILE_PAGE_NEXT"
  | "OPEN_DAILY_LOGIN_BONUS"
  | "CLOSE_DAILY_LOGIN_BONUS"
  | "CLAIM_DAILY_LOGIN_BONUS"
  | "DAILY_REWARD_CRATE_TAP"
  | "DAILY_REWARD_CONTINUE"
  | "OPEN_AUTH_GATE"
  | "OPEN_PROFILE_SETTINGS"
  | "PROFILE_FILTER_ALL"
  | "PROFILE_FILTER_WALLET"
  | "TOGGLE_APPEAR_OFFLINE"
  | "CHANGE_USERNAME"
  | "WALLET_CONNECT"
  | "WALLET_UNLINK"
  | "NFT_SYNC"
  | "AVATAR_SELECT_NFT"
  | "SOL_WALLET_CONNECT"
  | "SOL_WALLET_DISCONNECT"
  | "OPEN_TOKEN_SHOP"
  | "TOKEN_SHOP_BACK"
  | "TOKEN_SHOP_SELECT"
  | "TOKEN_SHOP_CONFIRM"
  | "TOKEN_SHOP_CANCEL"
  | "TOKEN_SHOP_DISMISS"
  | "TOKEN_SHOP_RECHECK"
  | "AD_GATE_WATCH_AD"
  | "STAMINA_HEART_TAP"
  | "AD_GATE_CONNECT_WALLET"
  | "SHARE_REFERRAL"
  | "INSTALL_APP"
  | "TOGGLE_PUSH_NOTIFICATIONS"
  | "OPEN_WORLD_MAP"
  | "WORLD_MAP_BACK"
  | "SHOP_PREV"
  | "SHOP_NEXT"
  | "PROFILE_BROWSE_PREV"
  | "PROFILE_BROWSE_NEXT"
  | "PROFILE_VIEW_ALL"
  | "PROFILE_VIEW_BROWSE"
  | "PROFILE_EDIT_SKIN"
  | "PROFILE_VINYL_FLIP"
  | "OPEN_PHONE"
  | "PHONE_BACK"
  | "PHONE_TAB_MESSAGES"
  | "PHONE_TAB_FRIENDS"
  | "PHONE_TAB_HIGHSCORES"
  | "PHONE_TAB_SETTINGS"
  | "PHONE_TAB_WALLET"
  | "PHONE_TAB_TRAVEL"
  | "OPEN_MESSAGE"
  | "MESSAGE_BACK"
  | "MESSAGE_ACTION"
  | "MESSAGE_ACTION_SECONDARY"
  | "TAP_MESSAGE_ALERT"
  | "DISMISS_MESSAGE_ALERT"
  | "OPEN_FRIEND_DETAIL"
  | "FRIEND_DETAIL_BACK"
  | "FRIEND_DETAIL_REMOVE"
  | "FRIEND_DETAIL_BATTLE"
  | "PHONE_ADD_FRIEND_BACK"
  | "PHONE_EDIT_USERNAME_BACK"
  | "VERSUS_REMATCH"
  | "VERSUS_EXIT"
  | "PHONE_ADD_FRIEND_SUBMIT"
  | "PHONE_REFER_FRIEND"
  | "PHONE_REFER_BACK"
  | "PHONE_REFER_SHARE"
  | "PHONE_REFER_COPY"
  | "ONBOARDING_ACCEPT_CALL"
  | "ONBOARDING_TAP_ADVANCE"
  | "ONBOARDING_SKIP"
  | "RAFSBY_WORLD_TOUR"
  | "RAFSBY_HOW_TO_PLAY"
  | "DEBUG_RESET_ONBOARDING"
  | "PAUSE_RESUME"
  | "PAUSE_SETTINGS"
  | "PAUSE_RESTART"
  | "PAUSE_QUIT"
  | "PAUSE_SETTINGS_BACK"
  | "REJOIN_CANCEL"
  | "SCORE_RETRY_SUBMIT";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface UiRects {
  board?: Rect;
  boardGridSize?: number;
  start?: Rect;
  continue?: Rect;
  leaderboardToggle?: Rect;
  gameOverNext?: Rect;
  versusResultNext?: Rect;
  versusRematch?: Rect;
  versusExit?: Rect;
  soloSelectorBlocker?: Rect;
  soloSelectorClose?: Rect;
  soloStartClassic?: Rect;
  soloStartRowRush?: Rect;
  soloStartClearout?: Rect;
  soloStartColorHunt?: Rect;
  soloStartTurboMaxxing?: Rect;
  soloContinueClassic?: Rect;
  soloContinueRowRush?: Rect;
  soloContinueClearout?: Rect;
  soloContinueColorHunt?: Rect;
  soloContinueTurboMaxxing?: Rect;
  music?: Rect;
  musicVolume?: Rect;
  sfxVolume?: Rect;
  pause?: Rect;
  characterHub?: Rect;
  opponentHub?: Rect;
  rotateLeft?: Rect;
  rotateRight?: Rect;
  keyTrigger?: Rect;
  grooveMeter?: Rect;
  grooveMixer?: Rect;
  goalChip?: Rect;
  skipTutorial?: Rect;
  tutorial?: Rect;
  loginGoogle?: Rect;
  loginDiscord?: Rect;
  logout?: Rect;
  mainMenu?: Rect;
  headerProfile?: Rect;
  settings?: Rect;
  settingsBack?: Rect;
  settingsBgPrev?: Rect;
  settingsBgNext?: Rect;
  settingsBgStyle?: Rect;
  settingsBgBloom?: Rect;
  settingsBgGlow?: Rect;
  settingsBgSaturation?: Rect;
  settingsBgMix?: Rect;
  settingsBgMotion?: Rect;
  settingsPreset?: Rect;
  settingsSideFx?: Rect;
  settingsSideFxDensity?: Rect;
  settingsParticles?: Rect;
  settingsOpponent?: Rect;
  settingsFpsCap?: Rect;
  settingsDebugEnabled?: Rect;
  settingsDebugOverlayGameplay?: Rect;
  settingsDebugOverlayBot?: Rect;
  settingsDebugVerbosity?: Rect;
  settingsDebugDiagnostics?: Rect;
  settingsDebugPerfSafe?: Rect;
  editProfile?: Rect;
  leaderboard?: Rect;
  leaderboardScrollViewport?: Rect;
  leaderboardTabSolo?: Rect;
  leaderboardTabVersus?: Rect;
  soloLeaderboardClassic?: Rect;
  soloLeaderboardRowRush?: Rect;
  soloLeaderboardClearout?: Rect;
  soloLeaderboardColorHunt?: Rect;
  soloLeaderboardTurboMaxxing?: Rect;
  soloLeaderboardCycleNext?: Rect;
  versusMode?: Rect;
  versusReady?: Rect;
  versusLeave?: Rect;
  versusClose?: Rect;
  versusQuickmatch?: Rect;
  versusChat?: Rect;
  versusCreateLobby?: Rect;
  versusJoinLobby?: Rect;
  lobbyToggleJoin?: Rect;
  versusLobbyRows?: Rect[];
  versusRefreshLobbies?: Rect;
  versusForfeit?: Rect;
  versusOptionsBounds?: Rect;
  lobbyCodeInput?: Rect;
  lobbyChatInput?: Rect;
  shop?: Rect;
  shopBack?: Rect;
  profile?: Rect;
  shopBuy0?: Rect;
  shopBuy1?: Rect;
  shopBuy2?: Rect;
  shopBuy3?: Rect;
  shopBuy4?: Rect;
  shopBuy5?: Rect;
  shopEquip0?: Rect;
  shopEquip1?: Rect;
  shopEquip2?: Rect;
  shopEquip3?: Rect;
  shopEquip4?: Rect;
  shopEquip5?: Rect;
  shopExchange?: Rect;
  shopBuyGems0?: Rect;
  shopBuyGems1?: Rect;
  shopBuyGems2?: Rect;
  shopBuyGems3?: Rect;
  shopBuySol0?: Rect;
  shopBuySol1?: Rect;
  shopBuySol2?: Rect;
  shopBuySol3?: Rect;
  shopBuyIap0?: Rect;
  shopBuyIap1?: Rect;
  shopBuyIap2?: Rect;
  shopBuyIap3?: Rect;
  shopRestoreIap?: Rect;
  shopIapConfirm?: Rect;
  shopIapCancel?: Rect;
  shopIapDismiss?: Rect;
  coins?: Rect;
  gemShop?: Rect;
  staminaHeart?: Rect;
  gemShopBack?: Rect;
  settingsTutorial?: Rect;
  settingsLogout?: Rect;
  settingsMusic?: Rect;
  settingsSfx?: Rect;
  settingsHaptics?: Rect;
  profileEquip0?: Rect;
  profileEquip1?: Rect;
  profileEquip2?: Rect;
  profileEquip3?: Rect;
  profileEquip4?: Rect;
  profileEquip5?: Rect;
  profileEquip6?: Rect;
  profileEquip7?: Rect;
  profileEquip8?: Rect;
  profilePagePrev?: Rect;
  profilePageNext?: Rect;
  profileBack?: Rect;
  // Username pick
  usernameInput?: Rect;
  usernameClaim?: Rect;
  // Daily login bonus
  dailyLoginOpen?: Rect;
  dailyLoginClose?: Rect;
  dailyLoginClaim?: Rect;
  dailyLoginDismiss?: Rect;
  dailyLoginSignIn?: Rect;
  // Daily rewards screen
  dailyRewardDay0?: Rect;
  dailyRewardDay1?: Rect;
  dailyRewardDay2?: Rect;
  dailyRewardDay3?: Rect;
  dailyRewardDay4?: Rect;
  dailyRewardDay5?: Rect;
  dailyRewardDay6?: Rect;
  dailyRewardCrateTap?: Rect;
  dailyRewardContinue?: Rect;
  dailyRewardBack?: Rect;
  // Friends
  friends?: Rect;
  friendsBack?: Rect;
  openAddFriend?: Rect;
  addFriendBack?: Rect;
  addFriendInput?: Rect;
  addFriendSubmit?: Rect;
  friendBattle0?: Rect;
  friendBattle1?: Rect;
  friendBattle2?: Rect;
  friendBattle3?: Rect;
  friendBattle4?: Rect;
  friendAccept0?: Rect;
  friendAccept1?: Rect;
  friendAccept2?: Rect;
  friendDecline0?: Rect;
  friendDecline1?: Rect;
  friendDecline2?: Rect;
  friendRemove0?: Rect;
  friendRemove1?: Rect;
  friendRemove2?: Rect;
  friendRemove3?: Rect;
  friendRemove4?: Rect;
  friendRemoveConfirm?: Rect;
  friendRemoveCancel?: Rect;
  friendRemoveDismiss?: Rect;
  friendsPagePrev?: Rect;
  friendsPageNext?: Rect;
  // Profile settings
  profileSettings?: Rect;
  profileSettingsBack?: Rect;
  changeUsername?: Rect;
  toggleAppearOffline?: Rect;
  // Profile filter tabs
  profileFilterAll?: Rect;
  profileFilterWallet?: Rect;
  // Wallet view skins link
  walletViewSkins?: Rect;
  // Wallet connect/unlink/sync (shown on profile settings)
  walletConnect?: Rect;
  walletUnlink?: Rect;
  walletSync?: Rect;
  // Solana wallet connect/disconnect (shown on profile settings below ETH section)
  solWalletConnect?: Rect;
  solWalletDisconnect?: Rect;
  // Token shop navigation
  openTokenShop?: Rect;
  tokenShopBack?: Rect;
  // Token shop items + purchase flow (TOKEN_SHOP page)
  tokenShopItem?: Rect[];
  tokenShopConfirm?: Rect;
  tokenShopCancel?: Rect;
  tokenShopDismiss?: Rect;
  tokenShopRecheck?: Rect;
  // Ad gate
  adGateWatchAd?: Rect;
  adGateConnectWallet?: Rect;
  // Referral
  shareReferral?: Rect;
  // PWA install
  installApp?: Rect;
  // Push notifications
  togglePushNotifications?: Rect;
  // World map
  worldMap?: Rect;
  worldMapBack?: Rect;
  // Shop navigation
  shopPrev?: Rect;
  shopNext?: Rect;
  // Profile browse navigation
  profileBrowsePrev?: Rect;
  profileBrowseNext?: Rect;
  profileViewAll?: Rect;
  profileViewBrowse?: Rect;
  profileEditSkin?: Rect;
  profilePortrait?: Rect;
  profileVinyl?: Rect;
  titleWatchAd?: Rect;
  // Phone
  phone?: Rect;
  phoneBack?: Rect;
  phoneTabMessages?: Rect;
  phoneTabFriends?: Rect;
  phoneTabHighscores?: Rect;
  phoneTabSettings?: Rect;
  phoneTabWallet?: Rect;
  phoneBounds?: Rect;
  phoneTabTravel?: Rect;
  // Phone message detail
  messageBack?: Rect;
  messageAction?: Rect;
  messageActionSecondary?: Rect;
  messageAlert?: Rect;
  messageAlertDismiss?: Rect;
  // Inbox message rows (indexed by position)
  inboxRow0?: Rect;
  inboxRow1?: Rect;
  inboxRow2?: Rect;
  inboxRow3?: Rect;
  inboxRow4?: Rect;
  inboxRow5?: Rect;
  // Friend detail view
  friendDetailBack?: Rect;
  friendDetailRemove?: Rect;
  friendDetailBattle?: Rect;
  // Friend rows (tappable for detail)
  friendRow0?: Rect;
  friendRow1?: Rect;
  friendRow2?: Rect;
  friendRow3?: Rect;
  friendRow4?: Rect;
  // Phone add friend inline
  phoneAddFriendInput?: Rect;
  phoneAddFriendSubmit?: Rect;
  phoneAddFriendBack?: Rect;
  phoneEditUsernameBack?: Rect;
  phoneReferFriend?: Rect;
  phoneReferBack?: Rect;
  phoneReferShare?: Rect;
  phoneReferCopy?: Rect;
  // Onboarding
  onboardingAcceptCall?: Rect;
  onboardingTapZone?: Rect;
  onboardingSkip?: Rect;
  rafsbyWorldTour?: Rect;
  rafsbyHowToPlay?: Rect;
  debugResetOnboarding?: Rect;
  // Pause menu
  pauseMenuBounds?: Rect;
  pauseResume?: Rect;
  pauseSettings?: Rect;
  pauseRestart?: Rect;
  pauseQuit?: Rect;
  pauseSettingsBack?: Rect;
  // Score retry
  scoreRetry?: Rect;
  // Rejoin overlay
  rejoinCancel?: Rect;
  // Solo select carousel
  soloSelectBack?: Rect;
  soloCarouselPrev?: Rect;
  soloCarouselNext?: Rect;
  soloSelectPlay?: Rect;
}

export interface DragState {
  startCell: Position;
  canvasX: number;
  canvasY: number;
}

export interface LeaderboardScrollGesture {
  held: boolean;
  deltaY: number;
  lastInteractionMs: number;
}

export interface BoardBounds {
  x: number;
  y: number;
  size: number;
  tile: number;
  gridSize: number;
}

export interface ImmediateGestureCallbacks {
  onBoardClickQueued?: (click: Position) => void;
  onSwapQueued?: (move: MoveAttempt) => void;
  /** Called synchronously from pointerdown to preserve user gesture context for Web Share API. */
  onShareRequested?: () => void;
  /** Called synchronously from pointerdown to preserve user gesture context for clipboard. */
  onCopyRequested?: () => void;
}

/** Abstract input contract consumed by GameController.update(). */
export interface GameInput {
  consumeSwap(): MoveAttempt | null;
  consumeActions(): UiAction[];
  consumeBoardClick(): Position | null;
  getDragState(): DragState | null;
  getHoverCell(): Position | null;
  getHoveredPowerup(): PowerupKind | null;
  consumeMusicVolumeChange(): number | null;
  consumeSfxVolumeChange(): number | null;
  consumeRotationSelection(): RotationDir | null;
  consumeFriendBattleIndex(): number | null;
  consumeFriendAcceptIndex(): number | null;
  consumeFriendDeclineIndex(): number | null;
  consumeFriendRemoveIndex(): number | null;
  consumeShopBuyIndex(): number | null;
  consumeShopEquipIndex(): number | null;
  consumeShopBuyGemsIndex(): number | null;
  consumeProfileEquipIndex(): number | null;
  consumeAvatarSelectNftIndex(): number | null;
  consumeTokenShopItemIndex(): number | null;
  consumeTappedLobbyIndex(): number | null;
  consumeTappedInboxIndex(): number;
  /** Returns current radial menu hold state, or null if no hub is being held. */
  getRadialMenuState(): { kind: "powerup" | "trap"; hubX: number; hubY: number; pointerX: number; pointerY: number; holdElapsedMs: number; holdReached: boolean } | null;
  /** Returns and clears a pending radial release event (kind + final pointer + hub center). */
  consumeRadialRelease(): { kind: "powerup" | "trap"; pointerX: number; pointerY: number; hubX: number; hubY: number } | null;
  /** True if a radial release is queued but not yet consumed by the state machine. */
  hasPendingRadialRelease(): boolean;
  /** Returns the UI rect currently under the pointer (desktop hover), or null. */
  getHoveredRect(): Rect | null;
}

export interface ResolvedUiHit {
  action: UiAction | null;
  rect: Rect | null;
}

export function resolveSwipeIntentDirection(
  dragDxPx: number,
  dragDyPx: number,
  thresholdPx = activeBoardPixelSize() * SWIPE_INTENT_THRESHOLD_TILE_FRACTION,
  axisDominanceRatio = SWIPE_INTENT_AXIS_DOMINANCE_RATIO
): SwipeIntentDirection | null {
  const absDx = Math.abs(dragDxPx);
  const absDy = Math.abs(dragDyPx);
  if (Math.max(absDx, absDy) < thresholdPx) {
    return null;
  }
  if (absDx >= absDy * axisDominanceRatio) {
    return dragDxPx >= 0 ? "RIGHT" : "LEFT";
  }
  if (absDy >= absDx * axisDominanceRatio) {
    return dragDyPx >= 0 ? "DOWN" : "UP";
  }
  return null;
}

export function swipeIntentDirectionToTarget(
  start: Position,
  direction: SwipeIntentDirection,
  boardSize = 10
): Position | null {
  if (direction === "LEFT") {
    const col = start.col - 1;
    return col < 0 ? null : { row: start.row, col };
  }
  if (direction === "RIGHT") {
    const col = start.col + 1;
    return col >= boardSize ? null : { row: start.row, col };
  }
  if (direction === "UP") {
    const row = start.row - 1;
    return row < 0 ? null : { row, col: start.col };
  }
  const row = start.row + 1;
  return row >= boardSize ? null : { row, col: start.col };
}

export function inferSwipeIntentTarget(
  start: Position,
  dragDxPx: number,
  dragDyPx: number,
  boardSize = 10,
  thresholdPx = (activeBoardPixelSize() / Math.max(1, boardSize)) * SWIPE_INTENT_THRESHOLD_TILE_FRACTION
): Position | null {
  const absDx = Math.abs(dragDxPx);
  const absDy = Math.abs(dragDyPx);

  if (absDx >= absDy) {
    if (absDx < thresholdPx) {
      return null;
    }
    const step = dragDxPx >= 0 ? 1 : -1;
    const col = start.col + step;
    if (col < 0 || col >= boardSize) {
      return null;
    }
    return { row: start.row, col };
  }

  if (absDy < thresholdPx) {
    return null;
  }
  const step = dragDyPx >= 0 ? 1 : -1;
  const row = start.row + step;
  if (row < 0 || row >= boardSize) {
    return null;
  }
  return { row, col: start.col };
}
